import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { SEO } from "@/components/SEO";
import { useMyJobs, useDeleteJobPosting, useUpdateJobPosting, useCreateJobPosting } from "@/hooks/useMyJobs";
import { useJobCredits } from "@/hooks/useJobCredits";
import { PurchaseCreditsModal } from "@/components/jobs/PurchaseCreditsModal";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { BrutalBadge } from "@/components/ui/BrutalBadge";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Plus, Briefcase, Eye, Edit, Trash2, MoreVertical, 
  Clock, CheckCircle, XCircle, Users, CreditCard, Loader2, ArrowLeft,
  MousePointer, BarChart3, FileText, Calendar, Copy
} from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { format } from "date-fns";
import { it, enUS, es } from "date-fns/locale";
import type { Locale } from "date-fns";
import { useToast } from "@/hooks/use-toast";

const dateLocales: Record<string, Locale> = {
  it: it,
  en: enUS,
  es: es,
};

const CompanyJobs = () => {
  const { t, i18n } = useTranslation();
  const langKey = (i18n.resolvedLanguage ?? i18n.language).split("-")[0];
  const currentLocale = dateLocales[langKey] || it;
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: jobs, isLoading: loadingJobs, refetch: refetchJobs } = useMyJobs();
  const deleteJob = useDeleteJobPosting();
  const updateJob = useUpdateJobPosting();
  const createJob = useCreateJobPosting();
  const { data: credits, isLoading: creditsLoading } = useJobCredits();

  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<string | null>(null);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [jobToExtend, setJobToExtend] = useState<{ id: string; title: string } | null>(null);
  const [isExtending, setIsExtending] = useState(false);
  const [activeTab, setActiveTab] = useState<'published' | 'expired' | 'drafts'>('published');

  // Filtra i job in base al tab attivo
  const filteredJobs = jobs?.filter(job => {
    if (activeTab === 'published') {
      return job.status === 'active' || job.status === 'paused';
    } else if (activeTab === 'expired') {
      return job.status === 'expired';
    } else if (activeTab === 'drafts') {
      return job.status === 'draft';
    }
    return true;
  }) || [];

  // Handle URL params for extension feedback and auto-open extend dialog
  useEffect(() => {
    const extended = searchParams.get("extended");
    const action = searchParams.get("action");
    const jobIdParam = searchParams.get("job_id");

    if (extended === "success") {
      toast({
        title: t("jobs.manage.extendSuccess"),
        description: t("jobs.manage.extendSuccessDesc"),
      });
      refetchJobs();
      // Clean URL params
      searchParams.delete("extended");
      searchParams.delete("job_id");
      searchParams.delete("action");
      setSearchParams(searchParams, { replace: true });
    } else if (extended === "cancelled") {
      searchParams.delete("extended");
      searchParams.delete("action");
      setSearchParams(searchParams, { replace: true });
    } else if (action === "extend" && jobIdParam && jobs && jobs.length > 0) {
      // Auto-open extend dialog if action=extend and job_id is provided
      const jobToOpen = jobs.find(j => j.id === jobIdParam);
      if (jobToOpen && !extendDialogOpen) {
        setJobToExtend({ id: jobToOpen.id, title: jobToOpen.title });
        setExtendDialogOpen(true);
        // Clean URL params after opening
        searchParams.delete("action");
        searchParams.delete("job_id");
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams, t, toast, refetchJobs, jobs, extendDialogOpen]);

  if (!isLoading && !isAuthenticated) {
    navigate("/auth/login");
    return null;
  }

  const handleDelete = async () => {
    if (!jobToDelete) return;
    
    try {
      await deleteJob.mutateAsync(jobToDelete);
      toast({
        title: t("jobs.manage.deleteSuccess"),
        description: t("common.cannotBeUndone"),
      });
    } catch (error) {
      toast({
        title: t("common.error"),
        description: t("jobs.manage.deleteError", "Error deleting job"),
        variant: "destructive" as any,
      });
    } finally {
      setDeleteDialogOpen(false);
      setJobToDelete(null);
    }
  };

  const handleStatusChange = async (jobId: string, newStatus: 'active' | 'paused') => {
    try {
      await updateJob.mutateAsync({ id: jobId, status: newStatus });
      const messageKey = newStatus === 'paused' ? 'jobs.manage.pauseSuccess' : 'jobs.manage.resumeSuccess';
      toast({
        title: t(messageKey),
      });
    } catch (error) {
      toast({
        title: t("common.error"),
        variant: "destructive" as any,
      });
    }
  };

  const handleExtendJob = async () => {
    if (!jobToExtend) return;
    
    setIsExtending(true);
    try {
      const { data, error } = await supabase.functions.invoke('extend-job-posting', {
        body: { job_posting_id: jobToExtend.id },
      });
      
      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      }
    } catch (error: any) {
      toast({
        title: t("jobs.manage.extendError"),
        description: error.message,
        variant: "destructive" as any,
      });
    } finally {
      setIsExtending(false);
      setExtendDialogOpen(false);
      setJobToExtend(null);
    }
  };

  const handleDuplicateJob = async (job: any) => {
    if (!credits?.company_id) return;
    
    try {
      const duplicatedData = {
        company_id: credits.company_id,
        title: job.title + ' (Copia)',
        description: job.description,
        contract_type: job.contract_type,
        salary_range_min: job.salary_range_min,
        salary_range_max: job.salary_range_max,
        salary_period: job.salary_period,
        work_mode: job.work_mode,
        location_city: job.location_city,
        location_region: job.location_region,
        required_skills: job.required_skills || [],
        nice_to_have_skills: job.nice_to_have_skills || [],
        inclusive_benefits: job.inclusive_benefits || [],
        inclusivity_tags: job.inclusivity_tags || [],
        application_method: job.application_method,
        application_url: job.application_url,
        application_email: job.application_email,
        status: 'draft' as const,
      };
      
      await createJob.mutateAsync(duplicatedData);
      
      toast({
        title: t("common.duplicateSuccess"),
        description: t("common.duplicateSuccessDesc"),
      });
      
      setActiveTab('drafts');
    } catch (error) {
      toast({
        title: t("common.duplicateError"),
        variant: "destructive" as any,
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <BrutalBadge variant="success"><CheckCircle className="w-3 h-3 mr-1" /> {t("jobs.status.active")}</BrutalBadge>;
      case 'paused':
        return <BrutalBadge variant="warning"><Clock className="w-3 h-3 mr-1" /> {t("jobs.status.paused")}</BrutalBadge>;
      case 'expired':
        return <BrutalBadge variant="secondary"><XCircle className="w-3 h-3 mr-1" /> {t("jobs.status.expired")}</BrutalBadge>;
      case 'draft':
        return <BrutalBadge variant="secondary">{t("jobs.status.draft")}</BrutalBadge>;
      default:
        return <BrutalBadge variant="secondary">{status}</BrutalBadge>;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-surface">
      <SEO 
        title={t("jobs.manage.title") + " | Inclusia"} 
        description={t("jobs.manage.description")}
      />
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          {/* Breadcrumb */}
          <Breadcrumb className="mb-4">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/dashboard">{t("common.dashboard")}</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{t("jobs.manage.title")}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {/* Header con freccia indietro */}
          <div className="flex items-center gap-4 mb-2">
              <button
                onClick={() => navigate("/dashboard")}
                className="p-2 hover:bg-accent transition-colors rounded-lg"
                aria-label={t("common.back")}
              >
              <ArrowLeft className="w-6 h-6" />
            </button>
              <h1 className="font-heading text-3xl font-bold text-dark">
                {t("jobs.manage.title")}
              </h1>
            </div>
            <p className="text-muted-foreground mb-8 ml-14">
              {t("jobs.manage.description")}
            </p>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8 w-full">
            {credits && (
              <div className="flex items-center gap-2 px-4 py-2 bg-card border-[3px] border-dark">
                  <CreditCard className="w-5 h-5 text-primary" />
                  <span className="font-bold text-dark">{credits.credits_available}</span>
                  <span className="text-muted-foreground text-sm">{t("jobs.credits.credits")}</span>
                  <BrutalButton size="sm" variant="outline" onClick={() => setPurchaseModalOpen(true)}>
                    {t("jobs.credits.buyMore")}
                    <Plus className="w-4 h-4 ml-1" />
                </BrutalButton>
              </div>
            )}
            <Link to="/dashboard/company/jobs/create">
              <BrutalButton disabled={creditsLoading || (!credits) || credits.credits_available === 0}>
                {creditsLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                {t("jobs.manage.createNew")}
              </BrutalButton>
            </Link>
          </div>

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'published' | 'expired' | 'drafts')}>
            <TabsList className="w-full sm:w-auto p-0 h-auto mb-6 bg-transparent border-0">
              <TabsTrigger 
                value="published"
                className="border-r-[3px] border-dark data-[state=active]:bg-primary data-[state=active]:text-dark font-heading font-bold uppercase px-4 sm:px-6 py-3 rounded-none"
              >
                {t('jobs.manage.tabs.published')}
              </TabsTrigger>
              <TabsTrigger 
                value="expired"
                className="border-r-[3px] border-dark data-[state=active]:bg-primary data-[state=active]:text-dark font-heading font-bold uppercase px-4 sm:px-6 py-3 rounded-none"
              >
                {t('jobs.manage.tabs.expired')}
              </TabsTrigger>
              <TabsTrigger 
                value="drafts"
                className="data-[state=active]:bg-primary data-[state=active]:text-dark font-heading font-bold uppercase px-4 sm:px-6 py-3 rounded-none"
              >
                {t('jobs.manage.tabs.drafts')}
              </TabsTrigger>
            </TabsList>

            <TabsContent value={activeTab} className="mt-0">
              {loadingJobs ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-32 bg-card border-[3px] border-dark animate-pulse" />
                  ))}
                </div>
              ) : filteredJobs.length > 0 ? (
                <div className="space-y-4">
                  {filteredJobs.map((job) => (
                    <div 
                      key={job.id}
                      className="bg-card border-[3px] border-dark shadow-[4px_4px_0px_#101322] p-6"
                    >
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h2 className="font-heading text-xl font-bold text-dark">
                              {job.title}
                            </h2>
                            {getStatusBadge(job.status || 'draft')}
                          </div>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                            <span>{job.location_city}</span>
                            <span>•</span>
                            <span>{t(`jobs.contractTypes.${job.contract_type}`, job.contract_type)}</span>
                            {job.published_at && (
                              <>
                                <span>•</span>
                                <span>
                                  {t("jobs.detail.postedOn")} {format(new Date(job.published_at), 'd MMM yyyy', { locale: currentLocale })}
                                </span>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Container stats + azioni - su mobile in colonna, su desktop inline */}
                        <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-4">
                          {/* Riga 1: Stats/Insights */}
                          <div className="flex items-center gap-3 md:gap-4 flex-wrap justify-start md:justify-end">
                            {/* Views - sempre visibile */}
                            <div className="text-center min-w-[48px]">
                              <div className="flex items-center justify-center gap-1 text-muted-foreground">
                                <Eye className="w-4 h-4" />
                                <span className="font-bold text-dark">{job.total_views || 0}</span>
                              </div>
                              <span className="text-xs text-muted-foreground">{t("jobs.manage.stats.views")}</span>
                            </div>

                            {job.application_method === 'upload_cv' ? (
                              /* Per UPLOAD CV: mostra solo CV inviati */
                              <div className="text-center min-w-[48px]">
                                <div className="flex items-center justify-center gap-1 text-muted-foreground">
                                  <FileText className="w-4 h-4" />
                                  <span className="font-bold text-dark">{job.applications_count || 0}</span>
                                </div>
                                <span className="text-xs text-muted-foreground">{t("jobs.manage.stats.cvSent")}</span>
                              </div>
                            ) : (
                              /* Per EMAIL o LINK ESTERNO: mostra click stats */
                              <>
                                {/* Total clicks */}
                                <div className="text-center min-w-[48px]">
                                  <div className="flex items-center justify-center gap-1 text-muted-foreground">
                                    <MousePointer className="w-4 h-4" />
                                    <span className="font-bold text-dark">{job.total_clicks || 0}</span>
                                  </div>
                                  <span className="text-xs text-muted-foreground">{t("jobs.manage.stats.totalClicks")}</span>
                                </div>
                                {/* Unique clicks */}
                                <div className="text-center min-w-[48px]">
                                  <div className="flex items-center justify-center gap-1 text-muted-foreground">
                                    <Users className="w-4 h-4" />
                                    <span className="font-bold text-dark">{job.unique_clicks || 0}</span>
                                  </div>
                                  <span className="text-xs text-muted-foreground">{t("jobs.manage.stats.uniqueClicks")}</span>
                                </div>
                                {/* Average */}
                                <div className="text-center min-w-[48px]">
                                  <div className="flex items-center justify-center gap-1 text-muted-foreground">
                                    <BarChart3 className="w-4 h-4" />
                                    <span className="font-bold text-dark">
                                      {job.unique_clicks && job.unique_clicks > 0 
                                        ? ((job.total_clicks || 0) / job.unique_clicks).toFixed(1) 
                                        : '0'}
                                    </span>
                                  </div>
                                  <span className="text-xs text-muted-foreground">{t("jobs.manage.stats.avgClicks")}</span>
                                </div>
                              </>
                            )}
                          </div>

                          {/* Riga 2: Pulsanti azione */}
                          <div className="flex items-center gap-2 justify-end">
                            <Link to={`/jobs/${job.slug}`} target="_blank">
                              <BrutalButton variant="outline" size="sm">
                                <Eye className="w-4 h-4" />
                              </BrutalButton>
                            </Link>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <BrutalButton variant="outline" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </BrutalButton>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="border-[3px] border-dark">
                                <DropdownMenuItem asChild>
                                  <Link to={`/dashboard/company/jobs/${job.id}/edit`} className="flex items-center gap-2">
                                    <Edit className="w-4 h-4" />
                                    {t("common.edit")}
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDuplicateJob(job)}>
                                  <Copy className="w-4 h-4 mr-2" />
                                  {t("common.duplicateAsDraft")}
                                </DropdownMenuItem>
                                {job.application_method === 'upload_cv' && (
                                  <DropdownMenuItem asChild>
                                    <Link to={`/dashboard/company/jobs/${job.id}/applications`} className="flex items-center gap-2">
                                      <Users className="w-4 h-4" />
                                      {t("jobs.manage.applications")}
                                    </Link>
                                  </DropdownMenuItem>
                                )}
                                {job.status === 'active' && (
                                  <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'paused')}>
                                    <Clock className="w-4 h-4 mr-2" />
                                    {t("jobs.manage.actions.pause")}
                                  </DropdownMenuItem>
                                )}
                                {job.status === 'paused' && (
                                  <DropdownMenuItem onClick={() => handleStatusChange(job.id, 'active')}>
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    {t("jobs.manage.actions.resume")}
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                {/* Extend option - available for active, paused, or expired jobs */}
                                {(job.status === 'active' || job.status === 'paused' || job.status === 'expired') && (
                                  <DropdownMenuItem 
                                    onClick={() => { 
                                      setJobToExtend({ id: job.id, title: job.title }); 
                                      setExtendDialogOpen(true); 
                                    }}
                                  >
                                    <Calendar className="w-4 h-4 mr-2" />
                                    {t("jobs.manage.actions.extend")} ({t("jobs.manage.actions.extendPrice")})
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => { setJobToDelete(job.id); setDeleteDialogOpen(true); }}
                                  className="text-destructive"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  {t("common.delete")}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 bg-card border-[3px] border-dark">
                  <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                  <h2 className="font-heading text-2xl font-bold mb-2 text-dark">
                    {activeTab === 'published' && t("jobs.manage.noPublished")}
                    {activeTab === 'expired' && t("jobs.manage.noExpired")}
                    {activeTab === 'drafts' && t("jobs.manage.noDrafts")}
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    {activeTab === 'published' && t("jobs.manage.noPublishedDesc")}
                    {activeTab === 'expired' && t("jobs.manage.noExpiredDesc")}
                    {activeTab === 'drafts' && t("jobs.manage.noDraftsDesc")}
                  </p>
                  {activeTab === 'published' && (
                    <Link to="/dashboard/company/jobs/create">
                      <BrutalButton disabled={creditsLoading || (!credits) || credits.credits_available === 0}>
                        {creditsLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Plus className="w-4 h-4 mr-2" />
                        )}
                        {t("jobs.manage.createFirst")}
                      </BrutalButton>
                    </Link>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />

      <PurchaseCreditsModal 
        open={purchaseModalOpen} 
        onClose={() => setPurchaseModalOpen(false)} 
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="border-[3px] border-dark shadow-[6px_6px_0px_#101322] w-[90vw] sm:w-auto sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">{t("jobs.manage.deleteConfirm")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("common.cannotBeUndone")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-3">
            <BrutalButton
              variant="secondary"
              size="sm"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              variant="danger"
              size="sm"
              onClick={handleDelete}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t("common.delete")}
            </BrutalButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Extend Job Dialog */}
      <AlertDialog open={extendDialogOpen} onOpenChange={setExtendDialogOpen}>
        <AlertDialogContent className="border-[3px] border-dark shadow-[6px_6px_0px_#101322] w-[90vw] sm:w-auto sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-xl">
              {t("jobs.manage.extendConfirm")}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-base">
              {t("jobs.manage.extendConfirmDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {jobToExtend && (
            <div className="p-3 bg-primary/10 border-[2px] border-dark">
              <p className="font-heading font-bold text-dark">"{jobToExtend.title}"</p>
            </div>
          )}
          <AlertDialogFooter className="gap-3">
            <BrutalButton
              variant="secondary"
              size="sm"
              onClick={() => setExtendDialogOpen(false)}
            >
              {t("common.cancel")}
            </BrutalButton>
            <BrutalButton
              variant="primary"
              size="sm"
              onClick={handleExtendJob}
              disabled={isExtending}
            >
              {isExtending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Calendar className="w-4 h-4 mr-2" />
              )}
              {t("jobs.manage.actions.extend")} ({t("jobs.manage.actions.extendPrice")})
            </BrutalButton>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CompanyJobs;
